import {
  EmptyState,
  IconAdd,
  PrimaryButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@procurify/ui'
import classNames from 'classnames'

import { TableLoader } from '@webapp/components/TableLoader'
import { type CustomWidthOption } from '@webapp/components/TableLoader/TableLoader'
import { useProcurifyIntl, useFormatBudgetValues } from '@webapp/hooks'
import { type BudgetCategory } from '@webapp/models/BudgetCategory'
import budgetCategorySVG from './assets/budget-category.svg'
import messages from './messages'
import { BudgetCategoryListStyled } from './styles/BudgetCategoryListStyled'

export interface BudgetCategoryListItem extends BudgetCategory {
  isOverBudget?: boolean
}

export interface IBudgetCategoryListProps {
  items?: BudgetCategoryListItem[]
  onEmptyStateButtonClick?: () => void
  onRowClick?: (budgetCategory: BudgetCategoryListItem) => void
  selectedId?: BudgetCategory['id']
  isLoading?: boolean
}

const COLUMNS: CustomWidthOption[] = [
  { width: 150 },
  { width: 150, alignRight: true },
  { width: 70, alignRight: true },
]

export const BudgetCategoryList = ({
  items = [],
  isLoading = false,
  onEmptyStateButtonClick = () => undefined,
  onRowClick = () => undefined,
  selectedId,
}: IBudgetCategoryListProps) => {
  const intl = useProcurifyIntl()

  const { formatBudgetPercent, formatBudgetPrice } = useFormatBudgetValues()

  const hasBudgets = items.length > 0

  return (
    <BudgetCategoryListStyled>
      <Table
        tableBodyCellPadding={16}
        aria-label={intl.formatMessage(messages.tableAriaLabel)}
        className='budget-list'
      >
        <TableHead>
          <TableRow>
            <TableCell>{intl.formatMessage(messages.nameHeader)}</TableCell>

            <TableCell align='right' width={COLUMNS[1]?.width}>
              {intl.formatMessage(messages.remainingHeader)}
            </TableCell>
            <TableCell align='right' width={COLUMNS[2]?.width}>
              {intl.formatMessage(messages.percentageHeader)}
            </TableCell>
          </TableRow>
        </TableHead>

        {isLoading && <TableLoader rows={10} widths={COLUMNS} />}

        {!isLoading && !hasBudgets && (
          <TableBody zebra={false} hoverable={false}>
            <TableRow>
              <TableCell colSpan={COLUMNS.length + 1}>
                <EmptyState
                  header={intl.formatMessage(messages.emptyStateHeader)}
                  content={intl.formatMessage(messages.emptyStateContent)}
                  button={
                    <PrimaryButton
                      iconComponent={<IconAdd />}
                      onClick={() => onEmptyStateButtonClick()}
                    >
                      {intl.formatMessage(messages.emptyStateButtonText)}
                    </PrimaryButton>
                  }
                  imgUrl={budgetCategorySVG}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        )}

        {!isLoading && hasBudgets && (
          <TableBody zebra={false}>
            {items.map((budget) => {
              return (
                <TableRow
                  key={budget.uuid}
                  onClick={() => onRowClick(budget)}
                  selected={budget.id === selectedId}
                  className={classNames('budget', {
                    'budget--overage': budget.isOverBudget,
                  })}
                >
                  <TableCell>{budget.name}</TableCell>

                  <TableCell className='budget--amount' align='right'>
                    {formatBudgetPrice(budget.variance)}
                  </TableCell>
                  <TableCell className='budget--amount' align={'right'}>
                    {formatBudgetPercent(budget.variance_percentage)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        )}
      </Table>
    </BudgetCategoryListStyled>
  )
}
